﻿import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import { Col, Row, Tag, TagProps } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWaterLower } from '@fortawesome/pro-regular-svg-icons';

import { MapBrowserEvent } from 'ol';
import Map from 'ol/Map';
import Control from 'ol/control/Control';

import { getDepthFromEvent } from '../layers/ChartSubLayers/DepthLayerBase';
import BaseEvent from 'ol/events/Event';


export interface Props extends Pick<TagProps, "color"> {    
    map: Map | undefined,
    /** Optional: Function for formatting depth value. */
    format?: (depth: number) => string,
    id?: string,
    className?: string,
    style?: CSSProperties,    
    mobile?: boolean
}

const DepthLayerField: React.FC<Props> = ({
    map,
    format = depth => `${depth.toFixed(2)} m`,
    id,
    className = "depth-layer-field",
    style,         
    color = "default",
    mobile,
}) => {

    const container = useRef<HTMLDivElement>(null);
    const element = useRef<HTMLDivElement>(null);    
    const depthRef = useRef<number| undefined>();
   
    const [depth, setDepth] = useState<number>();
    const [formattedDepth, setFormattedDepth] = useState<string>("");

    useEffect(() => {
        if (map && element.current && container.current) {

            const control = new Control({
                target: container.current,
                element: element.current
            })

            map.addControl(control);

            map.getViewport()?.addEventListener("pointerout", pointerOut);
            map.on(["click", "pointermove", "singleclick"], pointerMove);
            updateDepth();

            return () => {                
                map.getViewport()?.removeEventListener("pointerout", pointerOut);
                map.un(["click", "pointermove", "singleclick"], pointerMove);
                
                try {
                    map.removeControl(control);
                }
                catch { }  
            };            
        }
    }, [map]);  

    useEffect(() => {
        depthRef.current = depth;
        setFormattedDepth(depth === undefined ? "No data" : format(depth));
    }, [depth]);

    const pointerMove = (evt: BaseEvent | Event) => updateDepth(evt);
    const pointerOut = () => updateDepth();

    const updateDepth = (evt?: BaseEvent | Event) => {
        if (evt instanceof MapBrowserEvent) {
            const depth = evt ? getDepthFromEvent(evt) : undefined;
            if (depth !== depthRef.current)
                setDepth(depth);
        }
    }   
     
    return (
        <Tag id={id} className={className}
            ref={container} color={color}
            style={{
                pointerEvents: 'auto',
                position: 'relative',
                userSelect: 'none',
                textAlign: "center",
                minWidth: 100,
                ...style,
                display: depth === undefined ? 'none' : undefined
            }}>
            <div ref={element}>
                <Row wrap={false} gutter={4}>
                    <Col flex='none'>
                        <FontAwesomeIcon icon={faWaterLower} />
                    </Col>
                    <Col flex='auto'>
                        {formattedDepth}
                    </Col>
                </Row>   
            </div>
        </Tag>
    )
}

export default observer(DepthLayerField);